export const MAX_STEP_SUMMARY_LENGTH = 120;
export const MAX_RECIPE_TITLE_LENGTH = 200;

import type { Quantity } from "./quantity";
import type { RecipeTag } from "./recipe-tags";

export type { Quantity, QuantityDimension } from "./quantity";
export type { RecipeTag } from "./recipe-tags";

export type StepKind = "prep" | "cook";

export type StepTimerDef = {
  id: string;
  label?: string;
  durationSeconds: number;
  checkpoints?: number[];
};

export type RecipeStep = {
  id: string;
  summary: string;
  details?: string;
  kind?: StepKind;
  ingredientsUsed?: string[];
  timers?: StepTimerDef[];
};

export type RecipeIngredient = {
  id: string;
  name: string;
  rawText?: string;
  quantity?: Quantity;
  preparation?: string;
  optional?: boolean;
};

export type Recipe = {
  id: string;
  title: string;
  source?: string;
  estimatedMinutes?: number;
  servings: number;
  equipment: string[];
  ingredients: RecipeIngredient[];
  steps: RecipeStep[];
  tags: RecipeTag[];
  createdAt: string;
  updatedAt: string;
};

export type RecipeSummary = {
  id: string;
  title: string;
  source?: string;
  estimatedMinutes?: number;
  servings: number;
  stepCount: number;
  tags: RecipeTag[];
  updatedAt: string;
};

export type RecipeInput = {
  title: string;
  source?: string;
  estimatedMinutes?: number;
  servings: number;
  equipment: string[];
  ingredients: RecipeIngredient[];
  steps: RecipeStep[];
  tags?: RecipeTag[];
};

export type CookSession = {
  id: string;
  recipeId: string;
  completedStepIds: string[];
  completedIngredientIds: string[];
  startedAt: string;
  updatedAt: string;
  endedAt?: string;
};

export type ValidationResult = { ok: true } | { ok: false; error: string };

export function createId(): string {
  return crypto.randomUUID();
}

export function validateRecipeInput(input: RecipeInput): ValidationResult {
  const title = input.title.trim();
  if (title.length === 0) {
    return { ok: false, error: "title_required" };
  }

  if (title.length > MAX_RECIPE_TITLE_LENGTH) {
    return { ok: false, error: "title_too_long" };
  }

  if (!Number.isInteger(input.servings) || input.servings < 1) {
    return { ok: false, error: "invalid_servings" };
  }

  if (input.estimatedMinutes !== undefined && (!Number.isInteger(input.estimatedMinutes) || input.estimatedMinutes < 1)) {
    return { ok: false, error: "invalid_estimated_minutes" };
  }

  if (input.steps.length === 0) {
    return { ok: false, error: "steps_required" };
  }

  for (const step of input.steps) {
    const stepResult = validateRecipeStep(step);
    if (!stepResult.ok) {
      return stepResult;
    }
  }

  const ingredientIds = new Set(input.ingredients.map((ingredient) => ingredient.id));
  for (const ingredient of input.ingredients) {
    if (ingredient.name.trim().length === 0) {
      return { ok: false, error: "ingredient_name_required" };
    }
  }

  for (const step of input.steps) {
    for (const ingredientId of step.ingredientsUsed ?? []) {
      if (!ingredientIds.has(ingredientId)) {
        return { ok: false, error: "unknown_ingredient_reference" };
      }
    }
  }

  return { ok: true };
}

export function validateRecipeStep(step: RecipeStep): ValidationResult {
  const summary = step.summary.trim();
  if (summary.length === 0) {
    return { ok: false, error: "step_summary_required" };
  }

  if (summary.length > MAX_STEP_SUMMARY_LENGTH) {
    return { ok: false, error: "step_summary_too_long" };
  }

  if (step.timers !== undefined) {
    for (const timer of step.timers) {
      if (!Number.isInteger(timer.durationSeconds) || timer.durationSeconds < 1) {
        return { ok: false, error: "invalid_timer_duration" };
      }
    }
  }

  return { ok: true };
}

export function ingredientIdsForStep(step: RecipeStep): string[] {
  return step.ingredientsUsed ?? [];
}

export function computeIngredientCompletion(
  recipe: Recipe,
  completedStepIds: readonly string[]
): string[] {
  const completedSteps = new Set(completedStepIds);
  const completedIngredients: string[] = [];

  for (const ingredient of recipe.ingredients) {
    const usingSteps = recipe.steps.filter((step) => ingredientIdsForStep(step).includes(ingredient.id));
    if (usingSteps.length === 0) {
      continue;
    }

    if (usingSteps.every((step) => completedSteps.has(step.id))) {
      completedIngredients.push(ingredient.id);
    }
  }

  return completedIngredients;
}

export function mergeIngredientCompletion(
  recipe: Recipe,
  completedStepIds: readonly string[],
  manualCompletedIngredientIds: readonly string[]
): string[] {
  const autoCompleted = new Set(computeIngredientCompletion(recipe, completedStepIds));
  const merged = new Set(manualCompletedIngredientIds);
  for (const ingredientId of autoCompleted) {
    merged.add(ingredientId);
  }
  return [...merged];
}
